import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Wix-template-style animated preview.
 * A tall full-page screenshot auto-scrolls inside a browser chrome while the
 * card is in view, with a subtle 3D tilt on pointer move. On hover the scroll
 * speeds through the whole page (like Wix template previews).
 */
export function BrowserShowcase({ src, url = "preview", caption, alt, scrollMs = 9000 }) {
  const wrapRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  // 3D tilt
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 150, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 150, damping: 18 });

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
    setHovered(false);
  };

  return (
    <figure className="group" data-testid="browser-showcase">
      <motion.div
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={onLeave}
        style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
        className="overflow-hidden rounded-[16px] border border-black/10 bg-white shadow-[0_30px_70px_-30px_rgba(2,12,8,0.35)] transition-shadow duration-300 group-hover:shadow-[0_40px_90px_-30px_rgba(5,150,105,0.35)]"
      >
        {/* chrome */}
        <div className="flex items-center gap-2 border-b border-black/10 bg-[#F3F5F4] px-3 py-2">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          </span>
          <span className="mx-auto flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-black/45 ring-1 ring-black/5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {url}
          </span>
        </div>

        {/* scrolling viewport */}
        <div className="relative h-[300px] overflow-hidden sm:h-[360px] lg:h-[420px]">
          <motion.img
            src={src}
            alt={alt}
            loading="lazy"
            className="absolute left-0 top-0 w-full select-none"
            draggable={false}
            initial={{ y: "0%" }}
            animate={hovered ? { y: "-72%" } : { y: ["-5%", "-72%", "-5%"] }}
            transition={
              hovered
                ? { duration: 3.2, ease: "easeInOut" }
                : { duration: scrollMs / 1000, ease: "linear", repeat: Infinity, repeatType: "loop" }
            }
          />
          {/* top/bottom subtle fade */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/70 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/70 to-transparent" />
          <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
            anteprima live
          </span>
        </div>
      </motion.div>
      {caption ? (
        <figcaption className="mt-3 text-xs text-tertiary">{caption}</figcaption>
      ) : null}
    </figure>
  );
}

/**
 * Phone-frame animated preview for mobile-first apps.
 */
export function PhoneShowcase({ src, caption, alt, scrollMs = 8000 }) {
  const [hovered, setHovered] = useState(false);
  return (
    <figure className="group flex flex-col items-center" data-testid="phone-showcase">
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative w-[220px] rounded-[2.2rem] border border-black/10 bg-[#0B1210] p-2.5 shadow-[0_30px_70px_-25px_rgba(2,12,8,0.45)]"
      >
        {/* notch */}
        <span className="absolute left-1/2 top-3 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/20" />
        <div className="relative h-[440px] overflow-hidden rounded-[1.7rem] bg-white">
          <motion.img
            src={src}
            alt={alt}
            loading="lazy"
            className="absolute left-0 top-0 w-full select-none"
            draggable={false}
            animate={hovered ? { y: "-78%" } : { y: ["0%", "-78%", "0%"] }}
            transition={
              hovered
                ? { duration: 3, ease: "easeInOut" }
                : { duration: scrollMs / 1000, ease: "linear", repeat: Infinity, repeatType: "loop" }
            }
          />
        </div>
      </div>
      {caption ? (
        <figcaption className="mt-3 text-center text-xs text-tertiary">{caption}</figcaption>
      ) : null}
    </figure>
  );
}
